import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { ExcelColumn, exportToExcel } from '../utils/exportToExcel';
import DataCell from './ui/DataCell';
import { ExpandableCard, ExpandableRow } from './ui/ExpandableRow';
import MobileRecordCard from './ui/MobileRecordCard';
import StatusBadge from './ui/StatusBadge';
import TableShell from './ui/TableShell';

export interface Column {
    key: string;
    label: string;
    render?: (value: any, row: any) => React.ReactNode;
    mobileLabel?: string;
    mobilePriority?: number;
    className?: string;
    type?: 'text' | 'status' | 'date' | 'number' | 'link' | 'action';
}

interface ResponsiveTableProps {
    columns: Column[];
    data: any[];
    emptyMessage?: string;
    loading?: boolean;
    className?: string;
    onRowClick?: (row: any) => void;
    linkTo?: (row: any) => string;
    title?: string;
    subtitle?: string;
    enableExport?: boolean;
    exportFileName?: string;
    compact?: boolean;
    hideHeader?: boolean;
    getRowId?: (row: any, index: number) => string | number;
    renderActions?: (row: any) => React.ReactNode;
    renderRowDetails?: (row: any) => React.ReactNode;
}

/** Generate a deterministic avatar color from a name */
const avatarColors = ['bg-primary-500', 'bg-success-500', 'bg-warning-500', 'bg-secondary-600', 'bg-info-500', 'bg-error-500'];
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getAvatarColor = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getInitials = (name: string): string => {
    return name
        .split(' ')
        .map(w => w.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
};

const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
    columns,
    data,
    emptyMessage = "No data available",
    loading = false,
    className = "",
    onRowClick,
    linkTo,
    title,
    subtitle,
    enableExport = false,
    exportFileName,
    compact = false,
    hideHeader = false,
    getRowId,
    renderActions,
    renderRowDetails,
}) => {
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const toggleRow = (index: number) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedRows(newExpanded);
    };

    const mobileColumns = [...columns].sort((a, b) =>
        (b.mobilePriority || 0) - (a.mobilePriority || 0)
    ).slice(0, 3);

    const handleExport = () => {
        if (!enableExport || !data.length) return;
        const excelColumns: ExcelColumn<any>[] = columns.map((col) => ({
            header: col.label,
            accessor: (row) => row[col.key],
        }));
        const fileName =
            exportFileName ||
            `${(title || 'table').toLowerCase().replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}.xlsx`;
        exportToExcel({ fileName, sheetName: title || 'Data', rows: data, columns: excelColumns });
    };

    // ── Loading State ──────────────────────────────────────────────
    if (loading) {
        return (
            <TableShell className={className}>
                <div className="p-6">
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="animate-pulse flex items-center space-x-4">
                                <div className="h-10 w-10 bg-monday-header rounded-lg" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-3.5 bg-monday-header rounded w-3/4" />
                                    <div className="h-3 bg-monday-header rounded w-1/2" />
                                </div>
                                <div className="h-7 w-16 bg-monday-header rounded" />
                            </div>
                        ))}
                    </div>
                </div>
            </TableShell>
        );
    }

    // ── Empty State ────────────────────────────────────────────────
    if (data.length === 0) {
        return (
            <TableShell className={className}>
                <div className="p-10 text-center">
                    <div className="mx-auto w-16 h-16 rounded-2xl bg-monday-header flex items-center justify-center mb-4 ring-1 ring-monday-border-light">
                        <svg className="h-8 w-8 text-monday-status-neutral" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                    </div>
                    <p className="text-monday-text text-base font-semibold mb-1">{emptyMessage}</p>
                    <p className="text-monday-text-muted text-sm">Start by adding your first record</p>
                </div>
            </TableShell>
        );
    }

    // ── Render Cell Value ──────────────────────────────────────────
    const renderValue = (column: Column, value: any, row: any) => {
        if (column.render) return column.render(value, row);

        switch (column.type) {
            case 'status': {
                return <StatusBadge status={String(value || '')} fullCell />;
            }
            case 'date':
                return (
                    <div className="flex items-center text-monday-text-muted text-sm">
                        {FiIcons.FiCalendar({ className: "mr-1.5 h-3.5 w-3.5" })}
                        <span>{value}</span>
                    </div>
                );
            case 'number':
                return (
                    <DataCell variant="numeric">
                        {value?.toLocaleString()}
                    </DataCell>
                );
            case 'link':
                return (
                    <Link
                        to={linkTo ? linkTo(row) : '#'}
                        className="text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {value}
                    </Link>
                );
            default:
                return <DataCell>{value}</DataCell>;
        }
    };

    return (
        <>
            {/* ── Desktop Table View ──────────────────────────────── */}
            <div className="hidden md:block">
                <TableShell
                    className={className}
                    title={!hideHeader ? title : undefined}
                    subtitle={!hideHeader ? subtitle : undefined}
                    actions={!hideHeader && enableExport ? (
                        <button
                            type="button"
                            onClick={handleExport}
                            className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold hover:bg-primary-100 transition-colors"
                        >
                            {FiIcons.FiDownload({ className: 'h-3.5 w-3.5 mr-1.5' })}
                            Export
                        </button>
                    ) : undefined}
                >
                    {hideHeader && enableExport && (
                        <div className="flex justify-end border-b border-monday-border-light bg-white/60 px-5 py-3">
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold hover:bg-primary-100 transition-colors"
                            >
                                {FiIcons.FiDownload({ className: 'h-3.5 w-3.5 mr-1.5' })}
                                Export
                            </button>
                        </div>
                    )}
                    <table className={`min-w-full ${compact ? 'table-fixed w-full' : 'w-max'}`}>
                        <thead>
                            <tr className="bg-monday-header">
                                {columns.map((column, i) => (
                                    <th
                                        key={column.key}
                                        className={`text-left text-xs font-semibold text-monday-text-muted uppercase border-b border-monday-border-light ${compact ? 'px-3 py-2 tracking-normal' : 'px-5 py-2.5 tracking-wider'
                                            } ${i === 0 ? 'rounded-tl-lg' : ''
                                            } ${i === columns.length - 1 ? 'rounded-tr-lg' : ''} ${column.className || ''}`}
                                    >
                                        {column.label}
                                    </th>
                                ))}
                                {renderActions && (
                                    <th className={`${compact ? 'px-3 py-2 tracking-normal' : 'px-5 py-2.5 tracking-wider'} text-right text-xs font-semibold uppercase text-monday-text-muted border-b border-monday-border-light`}>
                                        Actions
                                    </th>
                                )}
                                {renderRowDetails && (
                                    <th className={`${compact ? 'px-3 py-2 tracking-normal' : 'px-5 py-2.5 tracking-wider'} text-right text-xs font-semibold uppercase text-monday-text-muted border-b border-monday-border-light`}>
                                        Details
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, index) => {
                                const rowKey = getRowId ? getRowId(row, index) : (row.id || index);
                                const isExpanded = expandedRows.has(index);
                                return (
                                    <React.Fragment key={rowKey}>
                                        <tr
                                            className={`border-b border-monday-border-light last:border-b-0 hover:bg-monday-hover transition-colors duration-150 ${onRowClick || linkTo ? 'cursor-pointer' : ''
                                                }`}
                                            onClick={() => { if (onRowClick) onRowClick(row); }}
                                        >
                                            {columns.map((column) => (
                                                <td
                                                    key={column.key}
                                                    className={`${compact ? 'px-3 py-2.5' : 'px-5 py-3.5'} whitespace-nowrap ${column.className || ''}`}
                                                >
                                                    {renderValue(column, row[column.key], row)}
                                                </td>
                                            ))}
                                            {renderActions && (
                                                <td className={`${compact ? 'px-3 py-2.5' : 'px-5 py-3.5'} whitespace-nowrap text-right`}>
                                                    {renderActions(row)}
                                                </td>
                                            )}
                                            {renderRowDetails && (
                                                <td className={`${compact ? 'px-3 py-2.5' : 'px-5 py-3.5'} whitespace-nowrap text-right`}>
                                                    <button
                                                        type="button"
                                                        onClick={(event) => { event.stopPropagation(); toggleRow(index); }}
                                                        className="inline-flex items-center rounded-full p-1.5 text-monday-text-muted hover:bg-monday-header hover:text-monday-text"
                                                        aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                                                    >
                                                        {FiIcons.FiChevronDown({ className: `h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}` })}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                        {renderRowDetails && (
                                            <ExpandableRow isExpanded={isExpanded} colSpan={columns.length + (renderActions ? 1 : 0) + 1}>
                                                {renderRowDetails(row)}
                                            </ExpandableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </TableShell>
            </div>

            {/* ── Mobile Card View ────────────────────────────────── */}
            <div className={`md:hidden space-y-3 ${className}`}>
                {title && (
                    <div className="mobile-table-header">
                        <div>
                            <h3 className="mobile-table-title">{title}</h3>
                            {subtitle && <p className="mobile-table-subtitle">{subtitle}</p>}
                        </div>
                    </div>
                )}

                {data.map((row, index) => {
                    const statusColumn = columns.find(col => col.type === 'status');
                    const isExpanded = expandedRows.has(index);
                    const expandable = renderRowDetails || columns.length > 3;
                    const detailFields = columns
                        .filter(col => !mobileColumns.find(mc => mc.key === col.key))
                        .map((column) => ({
                            label: column.label,
                            value: renderValue(column, row[column.key], row),
                        }));
                    const expandContent = expandable && isExpanded ? (
                        <ExpandableCard isExpanded={isExpanded}>
                            {renderRowDetails ? (
                                renderRowDetails(row)
                            ) : (
                                <div className="grid grid-cols-1 gap-2">
                                    {detailFields.map((field) => (
                                        <div key={field.label} className="flex justify-between items-center py-1">
                                            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-monday-text-muted">
                                                {field.label}
                                            </span>
                                            <div className="text-xs text-monday-text text-right">
                                                {field.value}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ExpandableCard>
                    ) : null;

                    return (
                        <MobileRecordCard
                            key={getRowId ? getRowId(row, index) : (row.id || index)}
                            title={mobileColumns[0] ? renderValue(mobileColumns[0], row[mobileColumns[0].key], row) : ''}
                            subtitle={mobileColumns.slice(1, 3).map((column) => (
                                <span key={column.key} className="mr-3">
                                    <span className="font-medium">{column.mobileLabel || column.label}:</span>{' '}
                                    {renderValue(column, row[column.key], row)}
                                </span>
                            ))}
                            status={statusColumn ? renderValue(statusColumn, row[statusColumn.key], row) : undefined}
                            actions={(
                                <>
                                    {renderActions?.(row)}
                                    {linkTo && (
                                        <Link
                                            to={linkTo(row)}
                                            className="mobile-btn mobile-btn-primary flex-1 text-center py-2.5 text-sm"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            View Details
                                        </Link>
                                    )}
                                    {expandable && (
                                        <button
                                            className="mobile-btn mobile-btn-secondary flex-1 py-2.5 text-sm"
                                            onClick={(e) => { e.stopPropagation(); toggleRow(index); }}
                                        >
                                            {isExpanded ? 'Close' : 'Details'}
                                        </button>
                                    )}
                                </>
                            )}
                            expandContent={expandContent}
                            isExpanded={isExpanded}
                            onClick={() => { if (onRowClick) onRowClick(row); }}
                        />
                    );
                })}
            </div>
        </>
    );
};

export default ResponsiveTable;
